import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToastNotification } from 'hooks/useToastNotification';
import { useUpdateMapping } from '../../hooks';
import { useWizard } from '../../store';
import { ColumnMapping } from '../../types';
import { LOGICAL_FIELDS } from '../../utils';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	Footer,
	Label,
	Row,
	ScrollContainer,
	Select,
	Table,
} from '../../TicketImporter.styles';

export const ColumnMappingStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const [mapping, setMapping] = useState<ColumnMapping>(state.mapping);

	const { mutate, isLoading } = useUpdateMapping(state.importId as number, {
		onSuccess: () => {
			dispatch({ type: 'patch', payload: { mapping } });
			// Skip the Events step entirely when scope=fixed (single event mode).
			history.push(`${pathname}${state.scopeMode === 'fixed' ? '#5' : '#4'}`);
		},
		onError: () => showNotification({ type: TYPES.ERROR, message: 'Could not save mapping' }),
	});

	const headers = state.upload?.headers ?? [];
	const sample = state.upload?.sample_rows ?? [];

	const setField = (key: keyof ColumnMapping, value: string) => {
		setMapping((prev) => ({ ...prev, [key]: value || undefined }));
	};

	const onContinue = () => {
		// Validate required fields based on scope.
		const missing: string[] = [];
		for (const f of LOGICAL_FIELDS) {
			const isRequired =
				f.required || (state.scopeMode === 'per_row' && (f.key === 'event_name' || f.key === 'event_date'));
			if (isRequired && !mapping[f.key]) missing.push(f.label);
		}
		if (missing.length > 0) {
			showNotification({ type: TYPES.ERROR, message: `Missing required mappings: ${missing.join(', ')}` });
			return;
		}
		mutate({ column_mapping: mapping });
	};

	return (
		<>
			<Card>
				<CardTitle>3. Column mapping</CardTitle>
				<CardDescription>
					Map each field in our model to the corresponding column in your CSV. We&apos;ve pre-filled what we could
					detect — review and adjust.
				</CardDescription>

				{LOGICAL_FIELDS.map((f) => {
					const isRequired =
						f.required || (state.scopeMode === 'per_row' && (f.key === 'event_name' || f.key === 'event_date'));
					return (
						<Row key={f.key}>
							<Label>
								{f.label}
								{isRequired && <span className="req">*</span>}
								{f.description && <small style={{ color: '#9ca3af', fontWeight: 400 }}> — {f.description}</small>}
								<Select value={mapping[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)}>
									<option value="">— not mapped —</option>
									{headers.map((h) => (
										<option key={h} value={h}>
											{h}
										</option>
									))}
								</Select>
							</Label>
						</Row>
					);
				})}
			</Card>

			<Card>
				<CardTitle>Sample (first 10 rows)</CardTitle>
				<ScrollContainer>
					<Table>
						<thead>
							<tr>
								{headers.map((h) => (
									<th key={h}>{h}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{sample.map((row, i) => (
								<tr key={i}>
									{row.map((cell, j) => (
										<td key={j}>{cell}</td>
									))}
								</tr>
							))}
						</tbody>
					</Table>
				</ScrollContainer>
			</Card>

			<Footer>
				<Button variant="secondary" onClick={() => history.push(`${pathname}#2`)}>
					← Back
				</Button>
				<Button onClick={onContinue} disabled={isLoading}>
					Continue →
				</Button>
			</Footer>
		</>
	);
};
